/* eslint max-lines: ["error", {"max": 450, "skipComments": true}] */
import { Dispatch } from 'redux';
import { push } from 'connected-react-router';
import axios, { CancelTokenSource } from 'axios';
import { getSearchQueryParamsFromState, buildSearchUrl } from '../utils';
import actions from '../../../state/actions';
import { reportSearch } from './searchReport';
import { shouldReportError } from './errorsUtils';
import {
  ajaxSearch,
  ajaxSearchCount,
  checkIfWatcherAlreadyExists,
  ErrorType,
} from '../search.api';

import {
  SYNC_QUERY_RESPONSE,
  SEARCH_REQUEST,
  PAGINATION_REQUEST,
  SYNC_PAGINATION_RESPONSE,
  SYNC_COUNTS,
  SEARCH_ERROR,
  CANCEL_REQUEST,
  PAGINATION_ERROR,
  CANCEL_PAGINATION_REQUEST,
  SWITCH_SEARCH_FEATURES,
} from '../action-types';
import { doctrineAnalytics } from '../../../utils/analyticsUtils';
import { StateType } from '../../../state/state.types';
import {
  SearchParams,
  SearchAPIResponse,
  SearchAPICount,
} from '../../search.types';
import {
  setWatcherExists,
  unsetWatcherExists,
} from '../../../watchers/state/actions';

let cancelSearch: CancelTokenSource;

let cancelPagination: CancelTokenSource;

let cancelCount: CancelTokenSource;
let cancelWatcherExistsRequest: CancelTokenSource;

const syncQueryResponse = (
  response: SearchAPIResponse,
  queryParams: StateType['queryParams'],
) =>
  <const>{
    type: SYNC_QUERY_RESPONSE,
    payload: {
      queryParams,
      response,
    },
  };

const syncPaginationResponse = (response: SearchAPIResponse) =>
  <const>{
    type: SYNC_PAGINATION_RESPONSE,
    payload: {
      response,
    },
  };

const searchRequest = (restrictedDocIds?: string[]) =>
  <const>{
    type: SEARCH_REQUEST,
    payload: {
      restrictedDocIds,
    },
  };

const paginationRequest = () =>
  <const>{
    type: PAGINATION_REQUEST,
  };

const syncCounts = (counts: SearchAPICount) =>
  <const>{
    type: SYNC_COUNTS,
    payload: {
      counts,
    },
  };

const searchError = (data: ErrorType) =>
  <const>{
    type: SEARCH_ERROR,
    payload: {
      data,
    },
  };

const paginationError = (data: ErrorType) =>
  <const>{
    type: PAGINATION_ERROR,
    payload: {
      data,
    },
  };
const cancelPaginationRequest = () =>
  <const>{
    type: CANCEL_PAGINATION_REQUEST,
  };

const cancelRequest = () =>
  <const>{
    type: CANCEL_REQUEST,
  };

export const switchSearchFeatures = () =>
  <const>{
    type: SWITCH_SEARCH_FEATURES,
  };

export const search = ({
  reason,
  updateURL = true,
  restrictedDocIds,
}: {
  reason: string;
  updateURL?: boolean;
  restrictedDocIds?: string[];
}) => (dispatch: Dispatch, getState: () => StateType) => {
  // Passing restrictedDocIds ensures that ids are persisted
  // when we are resetting other params via shouldResetParams.
  dispatch(searchRequest(restrictedDocIds));

  if (cancelPagination) {
    cancelPagination.cancel();
  }

  if (cancelSearch) {
    cancelSearch.cancel();
  }
  const state = getState();

  const {
    search: { redirectToSearchRoute },
    router: { location },
  } = state;

  const startRequestTime = new Date().getTime();

  const searchURL = buildSearchUrl(state.queryParams);

  if (updateURL) {
    if (!redirectToSearchRoute) {
      // Specific to search's article page, we want to persist the modal params
      // if there is one.
      // @ts-expect-error: see https://doctrinelegal.atlassian.net/browse/INS-1865
      const modalSearchParams = location.query?.modal
        ? // @ts-expect-error
          `&modal=${location.query?.modal}`
        : '';
      dispatch(
        push({
          pathname: location.pathname,
          search: `${searchURL.replace('/search', '')}${modalSearchParams}`,
        }),
      );
    } else {
      dispatch(
        push({
          pathname: '/search',
          search: searchURL.replace('/search', ''),
        }),
      );
    }
  }

  // We strim the query in order to disable searching for empty spaces
  const queryTrimmed = state.queryParams.userQuery.trim();
  // If query trimmed length is less than 2 characters, we don't want to perform a search
  if (queryTrimmed.length < 2) {
    dispatch(searchError({ error: 'IMPRECISE_SEARCH' }));
    return;
  }

  const params = getSearchQueryParamsFromState(state.queryParams);

  const [promise, cancelTokenSource] = ajaxSearch(params);

  cancelSearch = cancelTokenSource;

  promise
    .then((response) => {
      if (!response.data.error) {
        const { data } = response;

        reportSearch(data, params, {
          startRequestTime,
          reason,
          user: state.user,
        });

        if (
          data.aggregations &&
          data.aggregations.dates &&
          !data.aggregations.dates.length
        ) {
          // dates are handled very badly by search
          // dates not empty only if we have "in YEAR" in the query
          delete data.aggregations.dates;
        }

        dispatch(syncQueryResponse(data, state.queryParams));
      } else {
        // request too long or imprecise
        const thrown = { response };
        throw thrown;
      }
    })
    .catch((thrown) => {
      if (!axios.isCancel(thrown)) {
        if (thrown.response) {
          const { data } = thrown.response;
          dispatch(searchError(data));
          if (data && data.error === 'SEARCH_QUOTA_EXCEEDED') {
            dispatch(actions.user.setQuotaExceeded());
            doctrineAnalytics('Search LimitAppeared');
          } else if (data && data.error === 'TRIAL_EXPIRED') {
            dispatch(actions.user.setTrialExpired());
            doctrineAnalytics('Account TrialExpiredAppeared', {
              from: 'search',
            });
          } else if (
            data &&
            data.error === 'CANCELLED_BECAUSE_OF_NEWER_QUERY' &&
            window.Rollbar
          ) {
            window.Rollbar.error(
              'User received CANCELLED_BECAUSE_OF_NEWER_QUERY error during scroll',
            );
          }
        } else {
          throw thrown;
        }
      } else {
        dispatch(cancelRequest());
      }
    });

  const searchType = state.queryParams ? state.queryParams.type : '';
  const accessPremium = !!state.user.accessPremium;
  if (
    accessPremium &&
    (searchType === 'arret' || searchType === 'commentary')
  ) {
    checkIfWatcherExists(params)
      .then((response) => {
        const { key, searchQueries } = response.data;
        dispatch(
          setWatcherExists(
            key,
            searchQueries.map((searchQuery) => searchQuery.type),
          ),
        );
      })
      .catch((error) => {
        const notFoundError = error?.response?.status === 404;
        if (notFoundError) {
          dispatch(unsetWatcherExists());
        }
        if (!axios.isCancel(error) && !notFoundError) {
          throw error;
        }
      });
  }
};

export const searchNext = () => (
  dispatch: Dispatch,
  getState: () => StateType,
) => {
  dispatch(paginationRequest());
  const state = getState();
  const params = getSearchQueryParamsFromState(state.queryParams);

  const { query, autoCorrectedQuery } = state.results;
  params.q = autoCorrectedQuery || query;

  const [promise, cancelTokenSource] = ajaxSearch(params);

  cancelPagination = cancelTokenSource;

  promise
    .then((response) => {
      dispatch(syncPaginationResponse(response.data));
    })
    .catch((thrown) => {
      if (!axios.isCancel(thrown)) {
        if (thrown.response) {
          const { data } = thrown.response;
          dispatch(paginationError(data));
          if (data && data.error === 'SEARCH_QUOTA_EXCEEDED') {
            dispatch(actions.user.setQuotaExceeded());
            doctrineAnalytics('Search LimitAppeared');
          } else if (data && data.error === 'TRIAL_EXPIRED') {
            dispatch(actions.user.setTrialExpired());
            doctrineAnalytics('Account TrialExpiredAppeared', {
              from: 'pagination',
            });
          } else {
            throw thrown;
          }
        } else {
          throw thrown;
        }
      } else {
        dispatch(cancelPaginationRequest());
      }
    });
};

export const getSearchCount = () => (
  dispatch: Dispatch,
  getState: () => StateType,
) => {
  if (cancelCount) {
    cancelCount.cancel();
  }

  const state = getState();
  const params = getSearchQueryParamsFromState(state.queryParams);

  const { query, autoCorrectedQuery } = state.results;
  params.q = autoCorrectedQuery || query;

  const [promise, cancelTokenSource] = ajaxSearchCount(params);
  cancelCount = cancelTokenSource;
  return promise
    .then((response) => {
      dispatch(syncCounts(response.data));
    })
    .catch((thrown) => {
      if (shouldReportError(thrown)) {
        throw thrown;
      }
    });
};

function checkIfWatcherExists(params: SearchParams) {
  if (cancelWatcherExistsRequest) {
    cancelWatcherExistsRequest.cancel();
  }

  const [promise, cancelTokenSource] = checkIfWatcherAlreadyExists(params);

  cancelWatcherExistsRequest = cancelTokenSource;

  return promise;
}

export type SearchActions = ReturnType<
  | typeof syncQueryResponse
  | typeof syncPaginationResponse
  | typeof searchRequest
  | typeof paginationRequest
  | typeof syncCounts
  | typeof searchError
  | typeof paginationError
  | typeof cancelPaginationRequest
  | typeof cancelRequest
  | typeof switchSearchFeatures
>;
